import React, { useState, useEffect, useRef } from "react"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import DocumentMeta from "react-document-meta"

import { H2RButton, useIsPro } from "../imports"
import Layout from "../Layout"
import { H2RHeading } from "../imports"
import ProBadge from "./snippets/ProBadge"
import * as interactionsApi from '../../api/interactionsApi'
import * as cuesApi from '../../api/cue'
import { useFirestore } from "react-redux-firebase"
import { faArrowAltCircleRight, faCheck, faEllipsis, faPlus, faRefresh, faSpinner, faTrash, faXmarkCircle } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { PencilIcon } from "@heroicons/react/outline"
import moment from "moment"

export default function QandA() {
  let { projectId } = useParams()

  const selectedProject = useSelector(
    (state) => state.main.projects?.[projectId]
  )

  const meta = {
    title: `Q&A - ${selectedProject?.name}`,
  }

  if (selectedProject === undefined) {
    return null
  } else {
    return (
      <DocumentMeta {...meta}>
        <Layout>
          <section className={`mx-5 my-5 flex flex-col flex-1 overflow-auto`}>
            <div className="flex flex-col space-y-3 mx-3">
              <div className="flex justify-between">
                <H2RHeading type="h5">Q&A</H2RHeading>
              </div>
                <p className="text-gray-500 text-sm">Ask your audience anything</p>

                <QandAContents />

            </div>
          </section>
        </Layout>
      </DocumentMeta>
    )
  }
}


const QandAContents = () => {
  let { projectId } = useParams()
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState('')
  const isPro = useIsPro()

  const selectedProject = useSelector(
    (state) => state.main.projects?.[projectId]
  )
  const settings = useSelector(
    (state) => state.main.settings
  )

  const handleGenerateNewProject = async () => {
    setGenerating(true)
    setError('')

   const { data } = await interactionsApi.createNewInteractionsProject(settings.sync_user_id, settings.interactions_user_token, projectId)

   if (data.status === 'ERROR') {
    setGenerating(false)
    return setError(data.message)
   }

   if (data.status === 'SUCCESS' && data.interactionProjectId) {
    setGenerating(false)
    return cuesApi.updateProject(projectId, { interactionProjectId: data.interactionProjectId })
   }

   setGenerating(false)
   return setError('Something went wrong')
  }

  if (!isPro) {
    return (
      <p className="p-2 border border-gray-700 rounded"><ProBadge /> This feature is available with a pro licence.</p>
      )
    }
    
    if(!selectedProject.interactionProjectId) {
      return (
        <>
        <p>Generate a new Q&A project to get started.</p>
        {error && <p className="text-red-500">{error}</p>}
        <H2RButton
          text="Generate new project"
          size="small"
          colour="dark"
          onClick={handleGenerateNewProject}
          disabled={(!settings.interactions_user_token || !settings.sync_user_id) || generating}
          />
          {
            (!settings.interactions_user_token || !settings.sync_user_id) && <p className="text-sm text-gray-400 italic">Enter your User ID and Cloud Q&A token in settings.</p>
          }
        </>
    )
  }

  return (
    <InteractionProject user={settings.sync_user_id} token={settings.interactions_user_token} interactionProjectId={selectedProject.interactionProjectId} />
  )

}

let unlisteners = []
const InteractionProject = ({ user, token, interactionProjectId }) => {
  const { projectId } = useParams()
  const [loading, setLoading] = useState(false)

  const firestore = useFirestore()
  const interactionProjectData = useSelector(
    (state) => state.firestore?.data?.[interactionProjectId]
  )
  const interactionProjectQuestions = useSelector(
    (state) => state.firestore?.data?.[`${interactionProjectId}_questions`]
  )

  const handleConnectToProject = async () => {
    setLoading(true)
    // ROOT PROJECT
    const rootUnlistener = await firestore.onSnapshot({
      collection: `users/${user}/interactions/`,
      doc: `${interactionProjectId}`,
      storeAs: interactionProjectId
    })
    unlisteners.push(rootUnlistener)
    // QUESTIONS
    const questionsUnlistener = await firestore.onSnapshot({
      collection: `users/${user}/interactions/${interactionProjectId}/questions`,
      storeAs: `${interactionProjectId}_questions`
    })
    unlisteners.push(questionsUnlistener)

    return setLoading(false)

  }

  const handleAddNewQuestion = async (type) => {
    setLoading(true)
    await interactionsApi.addNewQuestion(interactionProjectId, user, token, type)
    setLoading(false)
  }
 
  const handleUpdateProject = async (update) => {
    setLoading(true)
    await interactionsApi.updateInteractionsProject( user, token, interactionProjectId, update)
    setLoading(false)
  }
  const handleUpdateQuestion = async (questionId, update) => {
    setLoading(true)
    await interactionsApi.updateQuestion( user, token, interactionProjectId, questionId, update)
    setLoading(false)
  }
  const handleDeleteQuestion = async (questionId) => {
    setLoading(true)
    await interactionsApi.deleteQuestion( user, token, interactionProjectId, questionId)
    setLoading(false)
  }

  const handleGenerateCode = async () => {
    setLoading(true)
    await interactionsApi.generateCode(user, token, interactionProjectId)
    setLoading(false)
  }

  function unsubscribeAll () {
    unlisteners.forEach((unsub) => unsub())
    unlisteners = []
  }

  useEffect(()=>{
    if(interactionProjectId){
      handleConnectToProject()
    }
    return () => unsubscribeAll()
  }, [interactionProjectId])


  console.log('interactionProjectData', interactionProjectData)
  useEffect(() => {
    console.log('Running updateDynamicText')
    if(interactionProjectData?.code) {
      cuesApi.updateDynamicText(projectId, 'qanda.code', interactionProjectData?.code)
    }
    if (interactionProjectData?.currentQuestion && interactionProjectQuestions?.[interactionProjectData?.currentQuestion]) {
      cuesApi.updateDynamicTexts(projectId, {
        'qanda.currentQuestionId': interactionProjectData?.currentQuestion,
        'qanda.currentQuestion': interactionProjectQuestions[interactionProjectData?.currentQuestion]?.question,
        'qanda.currentQuestionType': interactionProjectQuestions[interactionProjectData?.currentQuestion]?.type,
        'qanda.currentQuestionOptions': interactionProjectQuestions[interactionProjectData?.currentQuestion]?.options,
        'qanda.currentQuestionResults': interactionProjectQuestions[interactionProjectData?.currentQuestion]?.results,
        'qanda.currentQuestionResultsTotalCount': interactionProjectQuestions[interactionProjectData?.currentQuestion]?.totalCount,
        'qanda.currentQuestionResultsShowOnOutputIndex': interactionProjectQuestions[interactionProjectData?.currentQuestion]?.showOnOutputIndex,
      })

    } else {
      cuesApi.updateDynamicTexts(projectId, {
        'qanda.currentQuestionId': '',
        'qanda.currentQuestion': '',
        'qanda.currentQuestionType': '',
        'qanda.currentQuestionOptions': '',
        'qanda.currentQuestionResults': '',
        'qanda.currentQuestionResultsTotalCount': '',
        'qanda.currentQuestionResultsShowOnOutputIndex': ''
      })
    }

  }, [interactionProjectData, interactionProjectQuestions, projectId])

  return (
    <div className="relative flex flex-col gap-4">
      <div className="flex justify-between">
      <p className="select-text">Connected to: {interactionProjectData?.name} <span className="text-xs text-gray-500">(ID: {interactionProjectId})</span></p>
      {loading && <div className="animate-spin fixed left-[50%] top-16 z-50"><FontAwesomeIcon icon={faSpinner} /></div>}
      </div>

      {/* CODE */}
      {!interactionProjectData?.code || (interactionProjectData?.code && interactionProjectData?.expires?.seconds * 1000 - new Date().getTime() < 0) ?
        <div className="p-2 flex justify-between items-center bg-gray-800 rounded">
            <div className="flex flex-col">
              <p className="text-sm font-semibold">Generate a quick access code</p>
              <p className="text-xs text-gray-500">This 8 digit code will make it easier for audience members to join your Q&A session.</p>
            </div>
            <H2RButton text="Generate code" size="small" onClick={()=>handleGenerateCode()} />
        </div>
        :
        <div className="p-2 flex justify-between items-center bg-gray-800 rounded">
            <div className="flex flex-col">
              <p className="text-sm font-semibold">Quick access code</p>
              <p className="text-xs text-gray-500">This code will automatically expire {moment(interactionProjectData?.expires?.seconds * 1000).fromNow()}.</p>
            </div>
            <div className="select-text flex flex-col items-end">
              <p className="text-xs text-gray-500">h2r.li/code</p>
              <p className="font-bold">{interactionProjectData?.code}</p>
            </div>
        </div>
      }

      <span className="border-b border-gray-700"></span>
      <p className="font-semibold">Questions</p>

      <div className="flex flex-col gap-6">
        {interactionProjectQuestions && Object.entries(interactionProjectQuestions).map(([id, question]) => {
          if(!question) return null
          return (
            <InteractionsQuestion key={id} id={id} question={question} isCurrentQuestion={id === interactionProjectData?.currentQuestion} handleUpdateProject={handleUpdateProject} loading={loading} handleUpdateQuestion={handleUpdateQuestion} handleDeleteQuestion={handleDeleteQuestion} />
          )
        })}
      </div>

      <div className="flex gap-4">
        <H2RButton text="Open Q&A" colour="dark" size="small" icon={faPlus} disabled={loading} onClick={() => handleAddNewQuestion('openQandA')} />
        <H2RButton text="Emoji reaction" colour="dark" size="small" icon={faPlus} disabled={loading} onClick={() => handleAddNewQuestion('emoji')} />
        <H2RButton text="Poll" colour="dark" size="small" icon={faPlus} disabled={loading} onClick={() => handleAddNewQuestion('poll')} />
      </div>
    </div>
  )
}

export const OPEN_QANDA = 'openQandA'
export const EMOJI = 'emoji'
export const POLL = 'poll'

export const QUESTION_TYPES = {
  [OPEN_QANDA] : 'Open Q&A',
  [EMOJI]: 'Emoji reaction',
  [POLL]: 'Poll',
}

const InteractionsQuestion = ({ id, question, isCurrentQuestion = false, handleUpdateProject, loading, handleUpdateQuestion, handleDeleteQuestion }) => {
  const [showOptions, setShowOptions] = useState(false)

  const questionContents = {
    [OPEN_QANDA]: <OpenQandAContent questionId={id} options={question.options} results={question.results} loading={loading} showOnOutputIndex={question.showOnOutputIndex} handleUpdateQuestion={handleUpdateQuestion} />,
    [EMOJI]: <EmojiContent questionId={id} options={question.options} results={question.results} />,
    [POLL]: <PollContent questionId={id} options={question.options} results={question.results} totalCount={question.totalCount} onUpdate={(update)=>handleUpdateQuestion(id, update)} />,
  }

  return (
    <div className="p-2 flex flex-col gap-2 bg-gray-800/50 rounded">
      <div className="relative flex justify-between">
        <p className="text-sm font-semibold text-gray-500">{QUESTION_TYPES[question.type]}</p>
        <H2RButton text="" icon={faEllipsis} size="small" colour='dark' onClick={()=>setShowOptions(!showOptions)} />
        {showOptions &&
          <div className="p-2 absolute top-9 right-0 z-20 bg-black rounded">
            <div className="flex flex-col gap-2">
              <H2RButton text="Reset results" size="small" colour="dark" icon={faRefresh}
                onClick={()=>{
                  handleUpdateQuestion(id, { results: null})
                  setShowOptions(false)
                }}
              />
              <span className="border-b border-gray-800"></span>
              <H2RButton text="Delete question" size="small" colour="dark" icon={faTrash}
                onClick={()=>{
                  setShowOptions(false)
                  handleDeleteQuestion(id)
                }}
              />
            </div>
          </div>
        }
      </div>
      <p className="text-xl font-semibold"><EditableInput text={question.question} onUpdate={(update)=>handleUpdateQuestion(id, {question: update})} /></p>
      <span className="mt-2"></span>

      <div className="py-2">
        {questionContents[question.type]}
      </div>

      <H2RButton
        text={isCurrentQuestion ? "Clear current" : "Push to audience"}
        colour={isCurrentQuestion ? 'main' : 'dark'}
        size="small"
        icon={isCurrentQuestion ? faXmarkCircle : faArrowAltCircleRight}
        disabled={loading}
        onClick={() => {
          if(isCurrentQuestion) {
            return handleUpdateProject({ currentQuestion: null })
          } else {
            handleUpdateProject({ currentQuestion: id })
          }
        }}
      />
    </div>
  )
}

const EditableInput = ({ text, onUpdate }) => {
  const [editing, setEditing] = useState(false);
	const [q, setQ] = useState(text);

	const inputRef = useRef();

	const handleUpdate = () => {
		if (q === "" || q === text) {
			setQ(text);
			return setEditing(false);
		}
    onUpdate(q)
		setEditing(false);
	};

	useEffect(() => {
		setQ(text);
	}, [text]);

	useEffect(() => {
		if (editing === true) {
			inputRef.current.focus();
			inputRef.current.select();
		}
	}, [editing]);

  return (
    <>
    {editing === false ? (
					<button
						title="Edit"
						className="cursor-text flex space-x-2 items-center w-full group hover:underline"
						onClick={() => {
							return setEditing(true);
						}}
					>
						{q}
						<PencilIcon className="ml-2 h-5 w-5 text-gray-300 cursor-pointer opacity-0 group-hover:opacity-100" />
					</button>
				) : (
					<div className="flex space-x-2">
						<input
							ref={inputRef}
							className="w-full bg-transparent focus:outline-none focus:ring rounded"
							value={q}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									return handleUpdate();
								}
							}}
							onChange={(e) => {
								setQ(e.target.value);
							}}
						/>
						<H2RButton
							text="Done"
							colour="light"
							icon={faCheck}
							size="small"
							onClick={handleUpdate}
						/>
					</div>
				)}
      </>
  )
}

const EmojiContent = ({ options, results }) => {

  return (
    <div className="flex gap-4 justify-center">
      {options.map((option, index) => {
        return (
          <div
            key={option.id}
            className="bg-gray-800 p-4 pb-2 rounded text-4xl"
          >
            <div>{option.text}</div>
            <p className={[
              "pt-1 text-center text-base font-semibold",
              results?.[index] > 0 ? 'text-gray-300' : 'text-gray-500'

            ].join(' ')}>{results?.[index] || 0}</p>
          </div>
        )
      })}
    </div>
  )
}

export function calculatePercentage(x = 0, y = 0) {
  return `${Math.round((x / y) * 100)}%`;
 }


const PollContent = ({ options, results, totalCount, onUpdate }) => {

  const handleUpdatePollOption = (index, update) => {
    const newOptions = options.map((op, i) => {
      if(i === index) {
        return {
          ...op,
          text: update
        }
      } else {
        return op
      }
    })

    return onUpdate({options: newOptions})
  }

  const handleRemoveOption = (index) => {
    const newOptions = options.filter((op, i) => {
      if(i === index) {
        return false
      } else {
        return true
      }
    })

    return onUpdate({options: newOptions})
  }

  const handleAddPollOption = () => {
    let newOptions = Array.from(options)

    newOptions.push({ text: 'New option' })

    return onUpdate({options: newOptions})
  }

  return (
    <div className="flex flex-col space-y-2">
     <div
      className={`rounded flex justify-between items-center
     `}
     >

     </div>
     <div className="flex flex-col space-y-2">
      {options.map((option, index) => {
       return (
        <div
         key={index}
         className="relative rounded flex w-full items-center bg-gray-800"
        >
         <span
          style={{
           width: `${calculatePercentage(
            results?.[index],
            totalCount
           )}`,
          }}
          className="absolute rounded h-full w-100 opacity-40 z-0 transition-all duration-300 bg-gray-600"
         ></span>
         <span
          className="p-2 z-10 font-semibold"
         >
          {index + 1}
         </span>{" "}
         <div
          className="flex justify-between w-full"
         >
          <div className="w-full z-10"><EditableInput text={option.text} onUpdate={(update)=> handleUpdatePollOption(index, update)}/></div>{" "}
          <span className="mr-2 z-10 opacity-80">
           {results?.[index] > 0 &&
            `${calculatePercentage(results?.[index], totalCount)}`}
          </span>
         </div>
         <H2RButton text="" colour="dark" size="small" icon={faTrash} onClick={()=>handleRemoveOption(index)} />
        </div>
       );
      })}
      <div className="flex justify-end">
       <H2RButton text="Add option" colour="dark" size="small" icon={faPlus} onClick={handleAddPollOption} />
      </div>
     </div>
    </div>
   );
}

const OpenQandAContent = ({ questionId, options, results, showOnOutputIndex, handleUpdateQuestion }) => {

  if(!results) return <p className="italic opacity-70">Waiting for answers</p>
  return (
    <div className="flex flex-col gap-4 justify-left">
      {results.map((result, index) => {
        return (
          <div
            key={index}
            className="bg-gray-800 px-2 py-1 rounded text-xl w-full flex justify-between"
          >
            <div>{result.text} • <span className="text-sm">{result.name}</span></div>
            <H2RButton
              text={index === showOnOutputIndex ? 'Hide' : 'Show'}
              colour={index === showOnOutputIndex ? 'main' : 'dark'}
              size="small"
              onClick={() => {
                if(showOnOutputIndex === index) {
                  return handleUpdateQuestion(questionId, { showOnOutputIndex: null})
                } else {
                  handleUpdateQuestion(questionId, { showOnOutputIndex: index})
                }
              }}
            />
          </div>
        )
      })}
    </div>
  )
}